import React, { useState, useCallback, useRef, useEffect } from 'react';
import AiSettingsPanel from '../CanvasTools/AiSettingsPanel';
import { aiAPI } from '../../../services/api';
import { AI_MODELS, getModelById, getDefaultVoiceId } from '../../../config/aiModels';
import { estimateCreditCost } from '../../../config/creditPricing';
import { useAuth } from '../../../contexts/AuthContext';
import { X, Sparkles, Loader2 } from 'lucide-react';
import { ReactComponent as SettingsIcon } from '../../../assets/settings.svg';

// Map modal mediaType to AI model categories
const MEDIA_TYPE_TO_CATEGORIES = {
  image: ['image'],
  video: ['video'],
  audio: ['sound-effects', 'voiceover'],
};

const DEFAULT_MODEL_FOR_TYPE = {
  image: 'nano-banana',
  video: 'kling-silent',
  audio: 'sound-effects',
};

const QUEUED_MESSAGES = [
  'Waiting in queue...',
  'Your request is queued...',
  'Preparing generation...',
  'Almost there...',
];
const PROCESSING_MESSAGES = [
  'Generating...',
  'Creating your media...',
  'AI is working...',
  'Processing...',
];

const POLL_INTERVAL = 3000;

/**
 * MediaGenerateTab
 *
 * Self-contained AI generation UI for use inside MediaSelectorModal.
 * Handles prompt input, model settings, generation lifecycle (submit → poll → results),
 * and passes the selected result back via onSelect(url, name, thumbnailUrl).
 *
 * Props:
 *   mediaType  – 'image' | 'video' | 'audio'
 *   onSelect   – (url, name, thumbnailUrl) => void
 */
const MediaGenerateTab = ({ mediaType, onSelect }) => {
  const { user, updateCredits } = useAuth();

  // ── Generation state ──
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState(() => DEFAULT_MODEL_FOR_TYPE[mediaType] || 'kling-silent');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [outputCount, setOutputCount] = useState(1);
  const [length, setLength] = useState(() => {
    const m = getModelById(DEFAULT_MODEL_FOR_TYPE[mediaType]);
    return m?.settings?.length?.[0] || '5s';
  });
  const [voiceId, setVoiceId] = useState(getDefaultVoiceId());
  const [settingsOpen, setSettingsOpen] = useState(false);

  // ── Job lifecycle ──
  const [job, setJob] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const pollRef = useRef(null);
  const msgIndexRef = useRef(0);
  const msgTimerRef = useRef(null);

  // Models filtered by current media type
  const availableModelIds = AI_MODELS
    .filter(m => (MEDIA_TYPE_TO_CATEGORIES[mediaType] || []).includes(m.category))
    .map(m => m.id);

  // Reset when mediaType changes
  useEffect(() => {
    const defaultModel = DEFAULT_MODEL_FOR_TYPE[mediaType] || 'kling-silent';
    setPrompt('');
    setModel(defaultModel);
    setAspectRatio('16:9');
    setOutputCount(1);
    const cfg = getModelById(defaultModel);
    setLength(cfg?.settings?.length?.[0] || '5s');
    setVoiceId(getDefaultVoiceId());
    setSettingsOpen(false);
    setJob(null);
    setIsGenerating(false);
    setError(null);
  }, [mediaType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (msgTimerRef.current) clearInterval(msgTimerRef.current);
    };
  }, []);

  // Cycling status messages
  useEffect(() => {
    if (!isGenerating) {
      if (msgTimerRef.current) { clearInterval(msgTimerRef.current); msgTimerRef.current = null; }
      return;
    }
    const messages = job?.status === 'processing' ? PROCESSING_MESSAGES : QUEUED_MESSAGES;
    msgIndexRef.current = 0;
    setStatusMsg(messages[0]);
    msgTimerRef.current = setInterval(() => {
      msgIndexRef.current = (msgIndexRef.current + 1) % messages.length;
      setStatusMsg(messages[msgIndexRef.current]);
    }, 4000);
    return () => { if (msgTimerRef.current) clearInterval(msgTimerRef.current); };
  }, [isGenerating, job?.status]);

  // ── Handlers ──

  const handleModelChange = useCallback((id) => {
    setModel(id);
    const config = getModelById(id);
    if (config?.settings?.length && !config.settings.length.includes(length)) {
      setLength(config.settings.length[0]);
    }
    if (config?.settings?.aspectRatio && !config.settings.aspectRatio.includes(aspectRatio)) {
      setAspectRatio(config.settings.aspectRatio[0]);
    }
    if (config?.settings?.outputCount && !config.settings.outputCount.includes(outputCount)) {
      setOutputCount(config.settings.outputCount[0]);
    }
  }, [length, aspectRatio, outputCount]);

  const startPolling = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const poll = async () => {
      try {
        const res = await aiAPI.getJobStatus(jobId);
        const updated = res.data.data.job;
        setJob(updated);
        if (updated.status === 'completed' || updated.status === 'failed' || updated.status === 'cancelled') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setIsGenerating(false);
          if (updated.status === 'failed') setError(updated.error || 'Generation failed');
        }
      } catch (err) {
        console.error('AI poll error:', err);
      }
    };
    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL);
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || isGenerating) return;
    setError(null);
    setIsGenerating(true);
    setJob(null);

    const spriteId = `gen_modal_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    const modelConfig = getModelById(model);
    const settings = { model };
    if (modelConfig?.settings.aspectRatio) settings.aspectRatio = aspectRatio;
    if (modelConfig?.settings.outputCount) settings.outputCount = outputCount;
    if (modelConfig?.settings.length) settings.length = length;
    if (modelConfig?.settings.voices) settings.voiceId = voiceId;

    try {
      const res = await aiAPI.generate({ spriteId, prompt: prompt.trim(), settings, referenceMedia: [] });
      const newJob = res.data.data.job;
      const creditsRemaining = res.data.data.creditsRemaining;
      if (typeof creditsRemaining === 'number') {
        updateCredits(creditsRemaining);
      }
      setJob(newJob);
      startPolling(newJob._id);
    } catch (err) {
      setIsGenerating(false);
      setError(err.response?.data?.message || err.message || 'Failed to start generation');
    }
  }, [prompt, isGenerating, model, aspectRatio, outputCount, length, voiceId, startPolling, updateCredits]);

  const handleStop = useCallback(async () => {
    if (!job?._id || !isGenerating) return;
    try {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      const res = await aiAPI.cancelJob(job._id);
      setJob(res.data.data.job);
      setIsGenerating(false);
    } catch (err) {
      console.error('Failed to cancel:', err);
      setIsGenerating(false);
    }
  }, [job, isGenerating]);

  const handleSelectResult = useCallback(async (resultIndex) => {
    if (!job?._id) return;
    try {
      const res = await aiAPI.selectResult(job._id, resultIndex);
      const { media } = res.data.data;
      if (media?.url) {
        onSelect(media.url, `AI: ${prompt.trim().slice(0, 30)}`, media.thumbnailUrl || null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to select result');
    }
  }, [job, prompt, onSelect]);

  return (
    <div className="h-full flex flex-col">
      {/* Prompt + Settings */}
      <div className="p-3">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleGenerate(); } }}
          placeholder={`Describe the ${mediaType} you want to generate...`}
          disabled={isGenerating}
          className="w-full px-3 py-2.5 bg-ag-black-800 border border-ag-black-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-ag-green transition-colors resize-none disabled:opacity-50"
          rows={2}
        />

        {/* Controls row */}
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={() => setSettingsOpen(prev => !prev)}
            disabled={isGenerating}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-sourceSans transition-colors bg-ag-black-800 hover:bg-ag-black-700 border border-ag-black-600 disabled:opacity-50"
          >
            <SettingsIcon
              className={`w-[13px] h-[11px] transition-colors ${
                settingsOpen
                  ? '[&_circle]:stroke-white [&_path]:stroke-white'
                  : '[&_circle]:stroke-[#4A4A4A] [&_path]:stroke-[#4A4A4A]'
              }`}
            />
            <span className="text-gray-300">
              {getModelById(model)?.label || 'Settings'}
            </span>
          </button>

          <div className="flex-1" />

          {(() => {
            const cost = estimateCreditCost(model, { prompt, length, outputCount });
            const credits = user?.credits ?? 0;
            const canAfford = credits >= (cost || 0);

            if (isGenerating) {
              return (
                <button
                  onClick={handleStop}
                  className="px-4 py-1.5 bg-red-600 hover:bg-red-500 text-white text-sm font-sourceSans font-semibold rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <X className="w-3.5 h-3.5" />
                  Stop
                </button>
              );
            }

            return (
              <div className="flex items-center gap-2">
                {cost != null && (
                  <span className={`text-xs font-sourceSans whitespace-nowrap ${canAfford ? 'text-ag-green/70' : 'text-red-400'}`}>
                    ⚡ {cost}
                  </span>
                )}
                {canAfford ? (
                  <button
                    onClick={handleGenerate}
                    disabled={!prompt.trim()}
                    className="px-4 py-1.5 bg-ag-green text-black text-sm font-sourceSans font-semibold rounded-lg hover:bg-ag-green/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Generate
                  </button>
                ) : (
                  <a
                    href="/manage-subscription"
                    className="px-4 py-1.5 bg-red-600 text-white text-sm font-sourceSans font-semibold rounded-lg hover:bg-red-500 transition-colors flex items-center gap-1.5"
                  >
                    Upgrade
                  </a>
                )}
              </div>
            );
          })()}
        </div>

        {/* Inline settings panel */}
        {settingsOpen && (
          <div className="mt-2 rounded-lg overflow-hidden border border-ag-black-600">
            <AiSettingsPanel
              isOpen={true}
              selectedModel={model}
              onModelChange={handleModelChange}
              aspectRatio={aspectRatio}
              onAspectRatioChange={setAspectRatio}
              outputCount={outputCount}
              onOutputCountChange={setOutputCount}
              length={length}
              onLengthChange={setLength}
              selectedVoiceId={voiceId}
              onVoiceIdChange={setVoiceId}
              inline={true}
              compact={true}
              filterModels={availableModelIds}
            />
          </div>
        )}
      </div>

      {/* Results / Status area */}
      <div className="flex-1 overflow-y-auto px-3 pb-3" style={{ scrollbarWidth: 'thin', scrollbarColor: '#4A4A4A #1F1E1E' }}>
        {/* Error */}
        {error && (
          <div className="mb-3 px-3 py-2 bg-red-900/30 border border-red-700/50 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
            {error.includes && error.includes('credits') && (
              <a
                href="/manage-subscription"
                className="inline-block mt-1.5 text-xs font-sourceSans font-semibold text-ag-green hover:text-ag-green/80 underline"
              >
                Upgrade your plan →
              </a>
            )}
          </div>
        )}

        {/* Generating spinner */}
        {isGenerating && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 text-ag-green animate-spin mb-3" />
            <p className="text-gray-400 text-sm font-sourceSans">{statusMsg}</p>
          </div>
        )}

        {/* Results grid */}
        {!isGenerating && job?.status === 'completed' && job.results?.length > 0 && (
          <div>
            <p className="text-gray-400 text-xs font-sourceSans mb-2">Click a result to select it:</p>
            <div className="grid grid-cols-3 gap-2">
              {job.results.map((result, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectResult(index)}
                  className="relative group rounded-lg overflow-hidden border-2 border-transparent hover:border-ag-green transition-colors bg-ag-black-800 aspect-video"
                >
                  {result.thumbnailUrl ? (
                    <img crossOrigin="anonymous" src={result.thumbnailUrl} alt={`Result ${index + 1}`} className="w-full h-full object-cover" />
                  ) : result.mediaType === 'video' || mediaType === 'video' ? (
                    <video
                      crossOrigin="anonymous"
                      src={result.url}
                      className="w-full h-full object-cover"
                      muted
                      onMouseEnter={(e) => e.target.play()}
                      onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                    />
                  ) : result.mediaType === 'image' || mediaType === 'image' ? (
                    <img crossOrigin="anonymous" src={result.url} alt={`Result ${index + 1}`} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-ag-black-400" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <span className="text-white text-xs font-sourceSans font-semibold opacity-0 group-hover:opacity-100 transition-opacity">Select</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!isGenerating && !job && !error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Sparkles className="w-10 h-10 text-ag-black-400 mb-3" />
            <p className="text-gray-400 text-sm font-sourceSans">Describe what you want and click Generate</p>
            <p className="text-gray-500 text-xs font-sourceSans mt-1">
              {mediaType === 'video' }
              {mediaType === 'image' }
              {mediaType === 'audio' }
            </p>
          </div>
        )}

        {/* Cancelled */}
        {!isGenerating && job?.status === 'cancelled' && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-gray-400 text-sm font-sourceSans">Generation cancelled</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaGenerateTab;
